import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const Base_URL = process.env.REACT_APP_API_URL;

export const getNotifications = createAsyncThunk(
    'Notification/getNotifications',
    async (params, { rejectWithValue }) => {
        try {
            // params can be undefined, a userId string, or { userId, includeCleared }
            const userId = typeof params === 'string' ? params : params?.userId;
            const includeCleared = params?.includeCleared || false;
            const response = await axios.get(`${Base_URL}/Notification/getNotifications/${userId}?includeCleared=${includeCleared}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const markAllAsRead = createAsyncThunk(
    'Notification/markAllAsRead',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${Base_URL}/Notification/markAllAsRead`, { userId });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const deleteAllNotifications = createAsyncThunk(
    'Notification/deleteAllNotifications',
    async (userId, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${Base_URL}/Notification/deleteAll`, { userId });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const markAsRead = createAsyncThunk(
    'Notification/markAsRead',
    async ({ notificationId, userId }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${Base_URL}/Notification/markAsRead`, { notificationId, userId });
            return { ...response.data, notificationId };
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const updateNotificationPreferences = createAsyncThunk(
    'Notification/updateNotificationPreferences',
    async ({ userId, preferences }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${Base_URL}/Notification/updatePreferences`, { userId, preferences });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

const NotificationSlice = createSlice({
    name: 'Notification',
    initialState: {
        notifications: [], // For the top-right dropdown (polled)
        allNotifications: [], // For the View All page (full list)
        isLoading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        // getNotifications
        builder.addCase(getNotifications.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(getNotifications.fulfilled, (state, action) => {
            state.isLoading = false;
            const data = action.payload?.data || [];
            
            // If includeCleared was true, this came from the "View All" page
            if (action.meta.arg?.includeCleared) {
                state.allNotifications = data;
            } else {
                state.notifications = data;
            }
        });
        builder.addCase(getNotifications.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // markAllAsRead
        builder.addCase(markAllAsRead.pending, (state) => {
            // Keep loading subtle
        });
        builder.addCase(markAllAsRead.fulfilled, (state) => {
            // Clear both lists as requested
            state.notifications = [];
            state.allNotifications = [];
        });
        builder.addCase(markAllAsRead.rejected, (state, action) => {
            state.error = action.payload;
        });

        // markAsRead
        builder.addCase(markAsRead.fulfilled, (state, action) => {
            const updateList = (list) => list.map(n => 
                n._id === action.payload.notificationId ? { ...n, isUnread: false } : n
            );
            state.notifications = updateList(state.notifications);
            state.allNotifications = updateList(state.allNotifications);
        });

        // deleteAllNotifications
        builder.addCase(deleteAllNotifications.fulfilled, (state) => {
            state.notifications = [];
            state.allNotifications = [];
        });
    },
});

export default NotificationSlice.reducer;
